import MainContainer from "../../components/MainContainer";
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LinkIcon from "@material-ui/icons/Link";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import FormModal from "../../components/FormModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import Tooltip from "@material-ui/core/Tooltip";
import PageHelpGuide from "../../components/PageHelpGuide";
import { FORMS_HELP_DATA } from "../../constants/pageHelp/formsHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#ffffff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    zIndex: 1,
  
    position: "sticky",
    top: 0,},
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
}));

const FormsList = () => {
  const classes = useStyles();
  const [forms, setForms] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingForm, setDeletingForm] = useState(null);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const { data } = await api.get("/forms");
      setForms(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || i18n.t("forms.toasts.loadError") || "Error al cargar los formularios");
    }
  };

  const handleOpenModal = () => {
    setSelectedForm(null);
    setModalOpen(true);
  };

  const handleEdit = (form) => {
    setSelectedForm(form);
    setModalOpen(true);
  };

  const handleDeleteClick = (form) => {
    setDeletingForm(form);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingForm) return;
    try {
      await api.delete(`/forms/${deletingForm.id}`);
      toast.success(i18n.t("forms.toasts.deleted") || "Formulario eliminado con éxito.");
      fetchForms();
    } catch (err) {
      toast.error(err?.response?.data?.error || i18n.t("forms.toasts.deleteError") || "Error al eliminar");
    } finally {
      setDeletingForm(null);
    }
  };

  const handleCloseModal = () => {
    setSelectedForm(null);
    setModalOpen(false);
    fetchForms();
  };

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={FORMS_HELP_DATA}
              tooltipText="Guía y Ayuda de Formularios Web (Haz clic para ver)"
            />
            <Title>Formularios Dinámicos</Title>
          </div>
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              className={classes.applyButton}
              onClick={handleOpenModal}
            >
              Agregar
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>

        <TableContainer className={classes.scrollArea}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>ID</TableCell>
              <TableCell className={classes.tableHeader}>Nombre</TableCell>
              <TableCell className={classes.tableHeader}>Slug (Enlace)</TableCell>
              <TableCell className={classes.tableHeader} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map(form => (
              <TableRow key={form.id}>
                <TableCell>{form.id}</TableCell>
                <TableCell>{form.name}</TableCell>
                <TableCell>
                  <a href={`/f/${form.slug}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#00E5FF', textDecoration: 'none' }}>
                    <LinkIcon fontSize="small" /> /f/{form.slug}
                  </a>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar Formulario">
                    <IconButton size="small" onClick={() => handleEdit(form)}>
                      <EditIcon style={{ color: "#00a8ff" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar Formulario">
                    <IconButton size="small" onClick={() => handleDeleteClick(form)}>
                      <DeleteOutlineIcon style={{ color: "#e84118" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>

      <FormModal
        open={modalOpen}
        onClose={handleCloseModal}
        formId={selectedForm?.id}
      />

      <ConfirmationModal
        title={
          deletingForm
            ? `${i18n.t("forms.confirmationModal.deleteTitle") || "Confirmar Eliminación"} "${deletingForm.name}"?`
            : `${i18n.t("confirmationModal.title") || "Confirmar"}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDeleteConfirm}
      >
        {i18n.t("forms.confirmationModal.deleteMessage") || "¿Está seguro de que desea eliminar este formulario?"}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default FormsList;
